import React from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Toaster } from "sonner";
import {
  BadgeCheck, ShoppingBag, Share2, Megaphone, ArrowRight, GraduationCap,
  Fingerprint, Bot, Store, Sparkles,
} from "lucide-react";

/**
 * Post-registration onboarding for new Partners. Kit purchase leads the
 * flow — commissions accrue everywhere but withdrawals are locked until
 * the kit is admin-approved.
 */
export default function Welcome() {
  const { user } = useAuth();
  const firstName = (user?.full_name || "Partner").split(" ")[0];

  const steps = [
    {
      Icon: ShoppingBag,
      title: "1. Activate your Business Starter Kit",
      body: "Choose a kit (Starter ₱999 / Builder ₱2,499 / Executive ₱4,999), pay via GCash or Maya, and upload your receipt. Admin approves within one business day and your withdrawals unlock.",
      cta: "Choose My Kit",
      to: "/membership/purchase",
      testid: "welcome-cta-kit",
    },
    {
      Icon: Fingerprint,
      title: "2. Verify your identity",
      body: "Upload a government ID + selfie. Verification unlocks priority payouts and confirms your legal name matches your bank/e-wallet for withdrawals.",
      cta: "Complete Verification",
      to: "/kyc",
      testid: "welcome-cta-kyc",
    },
    {
      Icon: Store,
      title: "3. Browse the product catalog",
      body: "Explore Zenith's real product catalog — electronics, health, beauty, fashion, food, and lifestyle. Every listing includes wholesale + retail pricing and an instant share link.",
      cta: "Open Catalog",
      to: "/shop",
      testid: "welcome-cta-shop",
    },
    {
      Icon: Share2,
      title: "4. Share your affiliate link",
      body: "Copy your permanent affiliate link and drop it on Facebook, TikTok, Messenger, or anywhere your audience lives. Every sale routes commissions to your Commission Wallet automatically.",
      cta: "See Partner Rewards",
      to: "/rewards",
      testid: "welcome-cta-rewards",
    },
    {
      Icon: Megaphone,
      title: "5. Grab marketing assets",
      body: "The Marketing Center gives you ready-made captions, video scripts, images, and AI-generated copy — everything you need to post today.",
      cta: "Marketing Center",
      to: "/marketing",
      testid: "welcome-cta-marketing",
    },
  ];

  return (
    <div className="min-h-screen zfd-canvas px-4 sm:px-6 py-10" data-testid="welcome-root">
      <Toaster position="top-right" richColors />

      <div className="max-w-4xl mx-auto">
        <header className="text-center mb-10">
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-[#FBBF24] font-bold mb-3">
            <BadgeCheck size={14} /> Partner Account Activated
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-black tracking-tight" data-testid="welcome-greeting">
            Welcome to Zenith, {firstName}.
          </h1>
          <p className="text-white/65 mt-3 text-sm md:text-base max-w-2xl mx-auto">
            Your personal online store is live. Follow the four steps below and you can be
            making your first sale within the day.
          </p>
        </header>

        {/* Step cards */}
        <ol className="space-y-3 mb-10" data-testid="welcome-steps">
          {steps.map((s, i) => (
            <li key={s.title}>
              <Link
                to={s.to}
                className="glass-card p-5 flex items-start gap-4 border border-white/10 hover:border-[#FBBF24] hover:bg-[#FBBF24]/[0.03] transition group"
                data-testid={s.testid}
              >
                <div className="w-10 h-10 rounded-md bg-[#FBBF24]/12 border border-[#FBBF24]/35 flex items-center justify-center shrink-0">
                  <s.Icon size={18} className="text-[#FBBF24]" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-display font-black text-base">{s.title}</div>
                  <div className="text-sm text-white/70 leading-relaxed mt-1">{s.body}</div>
                  <div className="mt-3 inline-flex items-center gap-1 text-[11px] uppercase tracking-widest font-bold text-[#FBBF24] group-hover:translate-x-0.5 transition">
                    {s.cta} <ArrowRight size={13} />
                  </div>
                </div>
              </Link>
            </li>
          ))}
        </ol>

        {/* Bonus tips */}
        <section className="glass-card gold-border p-6 mb-8" data-testid="welcome-tips">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[#FBBF24] font-bold mb-3">
            <Sparkles size={12} /> Pro tips from the top partners
          </div>
          <ul className="space-y-2 text-sm text-white/80 leading-relaxed">
            <li className="flex items-start gap-2">
              <Bot size={14} className="text-[#FBBF24] mt-0.5 shrink-0" />
              Use Zen (the chat widget on the bottom-right) to generate captions and product descriptions in seconds.
            </li>
            <li className="flex items-start gap-2">
              <GraduationCap size={14} className="text-[#FBBF24] mt-0.5 shrink-0" />
              Post consistently. One reel or story per day beats one huge campaign per month.
            </li>
            <li className="flex items-start gap-2">
              <ShoppingBag size={14} className="text-[#FBBF24] mt-0.5 shrink-0" />
              Start with 3-5 hero products your friends and family would actually buy — then expand.
            </li>
          </ul>
        </section>

        <div className="text-center">
          <Link
            to="/dashboard"
            className="btn-deploy inline-flex items-center gap-2 px-8 py-3 rounded-md text-sm font-bold uppercase tracking-widest"
            data-testid="welcome-go-dashboard"
          >
            Go to Dashboard <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    </div>
  );
}
